import React from "react";
type Turnstile = {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
};
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}
let scriptReady: Promise<void> | null = null;
function getTurnstile() {
  const api = window.turnstile;
  // Named Window access can expose the settings section with id="turnstile".
  return typeof api?.render === "function" && typeof api?.remove === "function"
    ? api
    : undefined;
}
function loadScript() {
  if (getTurnstile()) return Promise.resolve();
  if (scriptReady) return scriptReady;
  scriptReady = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    const timeout = window.setTimeout(() => {
      script.remove();
      scriptReady = null;
      reject(new Error("Security check did not load. Try again."));
    }, 15000);
    script.onload = () => {
      window.clearTimeout(timeout);
      if (getTurnstile()) resolve();
      else {
        script.remove();
        scriptReady = null;
        reject(new Error("Security check unavailable"));
      }
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      script.remove();
      scriptReady = null;
      reject(new Error("Security check could not load. Try again."));
    };
    document.head.appendChild(script);
  });
  return scriptReady;
}
export default function TurnstileChallenge({
  siteKey,
  onToken,
  onError,
  resetKey = 0,
}: {
  siteKey: string;
  onToken: (token: string) => void;
  /** Called with the message on screen, and with "" once it clears. */
  onError?: (message: string) => void;
  resetKey?: number;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const callback = React.useRef(onToken);
  callback.current = onToken;
  const errorCallback = React.useRef(onError);
  errorCallback.current = onError;
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    errorCallback.current?.(error);
  }, [error]);
  const [retry, setRetry] = React.useState(0);
  const [compact, setCompact] = React.useState(false);
  React.useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    const measure = () => setCompact(host.getBoundingClientRect().width < 300);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  React.useEffect(() => {
    let disposed = false,
      widget: string | undefined,
      api: Turnstile | undefined;
    setError("");
    callback.current("");
    void loadScript().then(
      () => {
        api = getTurnstile();
        if (disposed || !ref.current || !api) return;
        // The script loaded, so a throw here is Turnstile refusing the widget, not the connection.
        try {
          widget = api.render(ref.current, {
            sitekey: siteKey,
            theme: "auto",
            size: compact ? "compact" : "flexible",
            callback: (token: string) => {
              if (!disposed) {
                setError("");
                callback.current(token);
              }
            },
            "expired-callback": () => {
              if (!disposed) {
                callback.current("");
                setError("Security check expired. Please try again.");
              }
            },
            "error-callback": () => {
              if (!disposed) {
                callback.current("");
                setError("Security check failed. Please try again.");
              }
            },
          });
        } catch {
          setError("Security check could not start. Please try again.");
        }
      },
      () => {
        if (!disposed)
          setError(
            "Security check could not load. Check your connection and try again.",
          );
      },
    );
    return () => {
      disposed = true;
      if (widget) api?.remove(widget);
    };
  }, [siteKey, resetKey, retry, compact]);
  return (
    <div className="portal-captcha">
      <div ref={ref} />
      {error && (
        <div role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="btn btn-ghost sm"
            onClick={() => setRetry((v) => v + 1)}
          >
            Retry security check
          </button>
        </div>
      )}
    </div>
  );
}
